import { MODAL_PUBLISH } from '@components/App/Modal/constants'
import { SettingsDropdown } from '@components/ArtBoard/Publish/components/SettingsDropdown'
import { SettingsSwitcher } from '@components/ArtBoard/Publish/components/SettingsSwitcher'
import {
  getUrl,
  publishResponseAdapter,
} from '@components/ArtBoard/Publish/helper'
import { usePublishData } from '@components/ArtBoard/Publish/hook/usePublishData'
import { NewTooltip, Panel } from '@lib/components'
import { IconChevronRight, IconPalette } from '@lib/icons'
import { Button, ThemeColors } from '@octopus-do/octopus-storybook'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { artBoardActions } from '@store/reducers/artboard'
import type { AppState } from '@store/types'
import {
  type PublishData,
  removePublishData,
  setPublishBranding,
  setPublishData,
  setPublishSearchEngine,
  setPublishTabInfo,
  type TabInfo,
} from '@storeOld/reducers/publish'
import { openModal } from '@storeOld/reducers/ui'
import type React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import ArrowUpRightIcon from '@/assets/images/icons/arrow-up-right.svg?react'
import CopyIcon from '@/assets/images/icons/copy-icon.svg?react'
import UpdateIcon from '@/assets/images/icons/update-icon.svg?react'
import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { useProjectAccess } from '@/hooks'
import { PUBLISH_PERMISSION } from '@/permissions/project/definitions'
import { deletePublishProject, publishProject } from '@/services/project'
import { GlobalActions } from '@/store'
import { clsJoin } from '@/utils'
import { formatDate } from '@/utils/date/formatDate'
import css from './Publish.module.scss'

export const Publish: React.FC = () => {
  const dispatch = useDispatch()
  const inputRef = useRef<HTMLInputElement>(null)

  const [loading, setLoading] = useState(false)

  const publishData = useSelector(migrationProjectSelector.getPublish)
  const publishUpdateIsLoading = useSelector(
    migrationProjectSelector.getPublishUpdateLoading
  )
  const tabs = useSelector<AppState, { id: string; name: string }[]>(
    (state) => {
      return migrationProjectSelector.getProjectTabs(state).map((tab) => ({
        ...tab,
        name: tab.title,
      }))
    }
  )
  const theme = useSelector(migrationProjectSelector.getProjectTheme)

  const id = useSelector(migrationProjectSelector.getProjectId)
  const { hasAccess } = useProjectAccess(PUBLISH_PERMISSION.BRANDING)

  const data = usePublishData(publishData?.tab?.id || '')

  const handleSetTabInfo = (data: TabInfo) => {
    dispatch(setPublishTabInfo(data))
  }

  useEffect(() => {
    if (publishData) {
      if (!publishData.tab && tabs.length > 0) {
        handleSetTabInfo({
          id: tabs[0].id,
          name: tabs[0].name,
        })
      }
    }
  }, [publishData])

  const handleClose = () => {
    dispatch(artBoardActions.changeMode('edit'))
  }

  const onCopyClick = async () => {
    if (!inputRef?.current) {
      return
    }

    inputRef.current.select()

    dispatch(GlobalActions.copy({ value: inputRef.current.value }))
  }

  const handleClickCustomize = () => {
    dispatch(
      openModal({
        modalType: MODAL_PUBLISH,
        modalProps: { edit: true, onPublishUpdate: handleRepublishClick },
      })
    )
  }

  const handleRepublishClick = async (publishData: PublishData | null) => {
    sendGtmEvent(GtmTriggerName.PUBLISH_UPDATE, 'Click Update')

    if (id && publishData) {
      try {
        setLoading(true)
        const response = await publishProject({
          ...publishData,
          content: data,
          theme,
        })

        dispatch(setPublishData(publishResponseAdapter(response)))
      } catch (e) {
        // biome-ignore lint/suspicious/noConsole: intentional
        console.log(e)
      } finally {
        setLoading(false)
      }
    }
  }

  const handleDeletePublish = async () => {
    sendGtmEvent(GtmTriggerName.PUBLISH_STOP, 'Click Stop publishing')

    if (id) {
      dispatch(artBoardActions.changeMode('edit'))
      const response = await deletePublishProject(id)

      if (response.isDeleted) {
        dispatch(removePublishData())
      }
    }
  }

  const handleFormatDate = useCallback(() => {
    if (publishData) {
      return formatDate(publishData.updatedAt)
    }
  }, [publishData])

  const origin = getUrl(id)

  const openPublishedSite = () => {
    sendGtmEvent(GtmTriggerName.PUBLISH_OPEN, 'Click Open')
    window.open(origin, '_blank', 'noopener')
  }

  return (
    <Panel
      title="Publish"
      containerClassName={clsJoin(css.container, ThemeColors.light)}
      onClose={handleClose}
    >
      <div className={css.body}>
        <div className={css.wrapper}>
          <div className={css.linkWrap}>
            <div className={css.inputWrap}>
              <input ref={inputRef} type="text" value={origin} />
              <NewTooltip
                content="Copy url"
                className={css.copyTooltip}
                renderEl={(onSetRef, onMouseEnter, onMouseLeave) => (
                  <button
                    ref={onSetRef}
                    className={css.copyBtn}
                    onClick={onCopyClick}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                  >
                    <CopyIcon />
                  </button>
                )}
              />
            </div>
            <div className={css.date}>
              <span className={css.dot} />
              Published {handleFormatDate()}
            </div>
          </div>

          <div className={css.customizeWrap} onClick={handleClickCustomize}>
            <IconPalette />
            <span className={css.title}>Customize</span>
            <div className={css.iconWrap}>
              <IconChevronRight />
            </div>
          </div>

          <div>
            {tabs.length > 1 && publishData?.tab && (
              <SettingsDropdown
                settingsName="Tab"
                items={tabs}
                activeItem={publishData.tab}
                onChange={handleSetTabInfo}
              />
            )}
            <SettingsSwitcher
              text="Search engine indexing"
              checked={!!publishData?.searchEngine}
              onChange={(value) => dispatch(setPublishSearchEngine(value))}
            />
            <SettingsSwitcher
              text="Octopus badge"
              checked={!!publishData?.withBranding}
              withBadge={!hasAccess}
              onChange={(value) => dispatch(setPublishBranding(value))}
            />
          </div>

          <div className={css.buttonsWrap}>
            <div className={css['w-100']}>
              <Button
                className={css['w-100']}
                size="medium"
                label="Update"
                left={UpdateIcon}
                loading={loading || publishUpdateIsLoading}
                onClick={() => handleRepublishClick(publishData)}
              />
            </div>
            <div className={css['w-100']}>
              <Button
                className={css['w-100']}
                priority="secondary"
                size="medium"
                label="Open"
                left={ArrowUpRightIcon}
                onClick={openPublishedSite}
              />
            </div>
          </div>
        </div>

        <div className={css.footer}>
          <button className={css.removeBtn} onClick={handleDeletePublish}>
            <i className="fa fa-circle-stop" />
            Stop publishing
          </button>
          <div />
        </div>
      </div>
    </Panel>
  )
}
